/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { ArticleCard } from '../ArticleCard/ArticleCard';
import { ArticleListInput } from './ArticlesList.types';
import { LinkButton } from '../LinkButton/LinkButton';
import { formatDateMonthYear } from '@/utils/date-formatting';
import { host } from '@/config';
import { ArticlePreview } from '@/types/article';

const HubListRow: React.FC<{ article: ArticlePreview; buttonText: string }> = ({ article, buttonText }) => (
  <div className="flex flex-col gap-2 md:flex-row md:items-center">
    <div className="flex flex-1 flex-col gap-2">
      <p className="text-dark my-0 text-[16px] leading-[1.4] font-semibold md:text-[18px]">{article.title}</p>
      {article.lastUpdated && (
        <p className="text-dark my-0 text-[16px] leading-[1.4] font-normal md:text-[18px] md:font-medium">
          {formatDateMonthYear(new Date(article.lastUpdated))}
        </p>
      )}
    </div>
    <a
      href={`${host}${article.url}`}
      className="border-dark-100 text-dark-200 flex h-[50px] w-[237px] shrink-0 items-center justify-center rounded-md border bg-white text-[18px] font-bold md:h-[60px]"
    >
      {buttonText}
    </a>
  </div>
);

export const ArticlesList: React.FC<ArticleListInput> = ({
  title,
  showAs = 'cards',
  buttonText = 'Go to article',
  builderState,
  tagVariant = 'primary',
}) => {
  const { articles, currentPage = 1, totalPages = 1, urlPrefix } = builderState.state.articles || {};

  if (!articles || !articles.length) return null;

  const getPageNumbers = (): (number | 'dots')[] => {
    const pages: (number | 'dots')[] = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    if (currentPage <= 3) {
      pages.push(1, 2, 3, 4, 'dots', totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1, 'dots', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, 'dots', currentPage - 1, currentPage, currentPage + 1, 'dots', totalPages);
    }

    return pages;
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div
        className={`flex flex-wrap items-center justify-center ${showAs === 'hub-list' ? 'gap-4 md:gap-10' : 'my-12 gap-10'}`}
      >
        <a
          data-testid="pagination-prev"
          href={currentPage > 1 ? `${urlPrefix}${currentPage === 2 ? '' : `/page/${currentPage - 1}`}` : '#'}
          className={`text-dark-100 hover:text-dark ${currentPage <= 1 ? 'pointer-events-none opacity-40' : ''}`}
          aria-disabled={currentPage <= 1}
        >
          <IconChevronLeft size={16} />
        </a>

        {getPageNumbers().map((item, idx) => {
          if (item === 'dots') {
            return (
              <span key={`dots-${idx}`} className="text-dark-100 px-2 font-semibold">
                ...
              </span>
            );
          }

          const isActive = item === currentPage;
          const isLastPage = item === totalPages;

          return (
            <a
              key={item}
              href={item !== 1 ? `${urlPrefix}/page/${item}` : `${urlPrefix}`}
              data-testid={isLastPage ? 'pagination-last-page' : `pagination-page-${item}`}
              className={`font-semibold ${isActive ? 'text-dark' : 'text-dark-100 hover:text-dark'}`}
            >
              {item}
            </a>
          );
        })}

        <a
          data-testid="pagination-next"
          href={currentPage < totalPages ? `${urlPrefix}/page/${currentPage + 1}` : '#'}
          className={`text-dark-100 hover:text-dark ${currentPage >= totalPages ? 'pointer-events-none opacity-40' : ''}`}
          aria-disabled={currentPage >= totalPages}
        >
          <IconChevronRight size={16} />
        </a>
      </div>
    );
  };

  const renderArticles = () => {
    if (showAs === 'cards') {
      return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {articles.map((article) => (
            <ArticleCard key={article.url} article={article} tagVariant={tagVariant} />
          ))}
        </div>
      );
    }

    if (showAs === 'hub-list') {
      return (
        <div className="flex flex-col gap-4">
          {articles.map((article) => (
            <HubListRow key={article.url} article={article} buttonText={buttonText} />
          ))}
        </div>
      );
    }

    return (
      <ul className="pl-0">
        {articles.map((article) => (
          <li key={article.url} className="flex justify-between">
            <div>
              <h3 className="mb-1 text-xl font-bold">{article.title}</h3>
              {article.lastUpdated && <small>{formatDateMonthYear(new Date(article.lastUpdated))}</small>}
            </div>
            <div>
              <LinkButton link={article.url} label={'Go to article'} size={'medium'} variant={'tertiary'} />
            </div>
          </li>
        ))}
      </ul>
    );
  };

  if (showAs === 'hub-list') {
    return (
      <div
        data-testid="article-list"
        className="tc-container bg-background-200 flex flex-col gap-4 py-6 md:gap-8 md:py-8"
      >
        {title && (
          <p
            data-testid="article-list-title"
            className="tc-subheading my-0"
            dangerouslySetInnerHTML={{ __html: title }}
          />
        )}
        {renderArticles()}
        {renderPagination()}
      </div>
    );
  }

  return (
    <div data-testid="article-list">
      {title && <h2 data-testid="article-list-title">{title}</h2>}
      {renderArticles()}
      {renderPagination()}
    </div>
  );
};
